import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import { selectActiveApp } from "@/app/globalSlices/appStripSlice";
import {
  selectActiveSessionCompanyName,
  selectIntacctId,
} from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import { NavigationItem } from "@/navigation/types";
import {
  ChevronRightIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  useSidebar,
} from "@/components/ui/sidebar";
import permissionsService from "@/services/permissionsService";
import { filterNavigationByAccess } from "@/utils/navigationHelpers";
import CompanySwitcherModal from "@/components/CompanySwitcherModal/CompanySwitcherModal";
import { toggleWideSidebarItem } from "@/app/globalSlices/wideSidebarSlice";
import { openFileManagerDialog } from "@/app/globalSlices/fileManagerDialogSlice";
import { cn } from "@/utils/utils";

// Animated Submenu Component
function AnimatedSubmenu({
  isOpen,
  children,
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  const [height, setHeight] = useState<number | undefined>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(isOpen ? contentHeight : 0);
    }
  }, [isOpen, children]);

  return (
    <div
      className="overflow-hidden transition-all duration-200 ease-out"
      style={{ height: height }}
    >
      <div ref={contentRef}>
        <SidebarMenuSub className="ml-3 border-l border-sidebar-border/40 pl-3 py-1">
          {children}
        </SidebarMenuSub>
      </div>
    </div>
  );
}

export default function WideSidebar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const openItems = useSelector(
    (state: RootState) => state.wideSidebar.openItems,
  );
  const activeApp = useSelector(selectActiveApp);

  const closeMobileNav = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const activeCompanyName = useSelector(selectActiveSessionCompanyName);
  const activeCompanyIntacctId = useSelector(selectIntacctId);

  const rawNav = getUnifiedNavigation();
  const { hasPermission, hasCertainPathCompany } = permissionsService();

  const filteredNav = filterNavigationByAccess(
    rawNav,
    hasPermission,
    hasCertainPathCompany(),
  );

  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);

  const canOpenCompanySwitcher = hasPermission("company-switcher");

  function handleToggleItem(itemKey: string) {
    dispatch(toggleWideSidebarItem(itemKey));
  }

  function isActiveRoute(href: string): boolean {
    if (href === "#") return false;
    if (href === "/hub") {
      return location.pathname === "/hub";
    }
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  }

  function renderNavigation(items: NavigationItem[], depth = 0) {
    return items.map((item) => {
      const itemKey = item.internalName || item.name;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = !!openItems[itemKey];
      const isActive = item.href ? isActiveRoute(item.href) : false;

      if (hasChildren) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              className={cn(
                "text-[13px] py-1.5 px-2 rounded-md",
                depth > 0 && "text-[12px]",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
              onClick={() => handleToggleItem(itemKey)}
            >
              <span className="flex-1">{item.name}</span>
              <ChevronRightIcon
                className={cn(
                  "h-3 w-3 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-90",
                )}
              />
            </SidebarMenuButton>
            <AnimatedSubmenu isOpen={isOpen}>
              {renderNavigation(item.children!, depth + 1)}
            </AnimatedSubmenu>
          </SidebarMenuItem>
        );
      }

      // External URL
      const isExternalUrl =
        item.href?.startsWith("http://") || item.href?.startsWith("https://");

      if (isExternalUrl) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              asChild
              className={cn(
                "text-[13px] py-1.5 px-2 rounded-md",
                depth > 0 && "text-[12px]",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
            >
              <a
                href={item.href}
                onClick={closeMobileNav}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className="flex-1">{item.name}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      // Dialog item
      if (item.isDialog) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              className={cn(
                "text-[13px] py-1.5 px-2 rounded-md",
                depth > 0 && "text-[12px]",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
              onClick={() => {
                closeMobileNav();
                if (item.dialogAction === "fileManager") {
                  dispatch(openFileManagerDialog());
                }
              }}
            >
              <span className="flex-1">{item.name}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      // Regular link
      return (
        <SidebarMenuItem key={itemKey}>
          <SidebarMenuButton
            asChild
            className={cn(
              "text-[13px] py-1.5 px-2 rounded-md",
              depth > 0 && "text-[12px]",
              isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
            )}
          >
            <Link onClick={closeMobileNav} to={item.href ?? "#"}>
              <span className="flex-1">{item.name}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });
  }

  // Extract navigation sections
  const hubSection = filteredNav.find((item) => item.internalName === "hub");
  const stochasticSection = filteredNav.find(
    (item) => item.internalName === "stochastic",
  );
  const eventRegistrationSection = filteredNav.find(
    (item) => item.internalName === "event-registration",
  );
  const internalAdminSection = filteredNav.find(
    (item) => item.internalName === "admin",
  );
  const companyAdminSection = filteredNav.find(
    (item) => item.internalName === "company-admin",
  );
  const adminSection = internalAdminSection || companyAdminSection;

  // Get the direct children items
  const hubItems = hubSection?.children || [];
  const eventItems = eventRegistrationSection?.children || [];
  const stochasticItems = stochasticSection?.children || [];
  const adminItems = adminSection?.children || [];

  return (
    <div className="flex-shrink-0 h-full">
      <Sidebar
        className="h-full border-r border-sidebar-border/60"
        collapsible="offcanvas"
        side="left"
        variant="sidebar"
      >
        <SidebarHeader className="h-14 border-b border-sidebar-border/60 p-3 flex justify-center">
          {canOpenCompanySwitcher ? (
            <button
              className="flex w-full items-center justify-between text-left hover:bg-sidebar-accent/50 rounded-md p-2 -m-2 transition-colors"
              onClick={() => setCompanySwitcherOpen(true)}
              type="button"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {activeCompanyName || "No Company"}
                </div>
                {activeCompanyIntacctId && (
                  <div className="text-xs text-muted-foreground">
                    ID: {activeCompanyIntacctId}
                  </div>
                )}
              </div>
              <ChevronUpDownIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
            </button>
          ) : (
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {activeCompanyName || "No Company"}
              </div>
              {activeCompanyIntacctId && (
                <div className="text-xs text-muted-foreground">
                  ID: {activeCompanyIntacctId}
                </div>
              )}
            </div>
          )}
        </SidebarHeader>

        <SidebarContent className="p-2 overflow-y-auto">
          {/* Hub section */}
          {(activeApp === "resources" || !activeApp) && hubItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>{renderNavigation(hubItems)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Events section */}
          {activeApp === "events" && eventItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>{renderNavigation(eventItems)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Stochastic section */}
          {activeApp === "stochastic" && stochasticItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>{renderNavigation(stochasticItems)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Administration section */}
          {activeApp === "admin" && adminItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>{renderNavigation(adminItems)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
      </Sidebar>

      <CompanySwitcherModal
        onClose={() => setCompanySwitcherOpen(false)}
        open={companySwitcherOpen}
      />
    </div>
  );
}
